import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Loader2, RefreshCw, Users, Home, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination, type PageMeta } from '@/components/Pagination';
import { apiCall, formatCents } from '@/lib/api';

interface HouseholdRow {
  id: string;
  name: string;
  primary_email: string | null;
  stripe_customer_id: string | null;
  member_count: number;
  active_count: number;
  created_at: string;
}

interface HouseholdMember {
  id: string;
  name: string;
  email: string | null;
  status: string;
  joined_at: string;
  plan_name: string | null;
  plan_amount_cents: number | null;
}

export default function Households() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<HouseholdRow[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<Record<string, HouseholdMember[]>>({});

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const r = await apiCall<{ items: HouseholdRow[] } & PageMeta>('/v1/households', {
        bearerToken: token, query: { page, per_page: 50 },
      });
      setItems(r.items);
      setPageMeta({ page: r.page, per_page: r.per_page, total: r.total, total_pages: r.total_pages });
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken, page]);

  useEffect(() => { void load(); }, [load]);

  async function toggle(id: string) {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (detail[id]) return;
    try {
      const token = await getToken();
      const r = await apiCall<{ members: HouseholdMember[] }>(`/v1/households/${id}`, { bearerToken: token });
      setDetail((d) => ({ ...d, [id]: r.members }));
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Home className="h-6 w-6" /> Households</h1>
          <p className="text-sm text-muted-foreground">
            Families sharing a billing contact. New signups with the same guardian email auto-attach and qualify for the sibling discount.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {err && <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {err}</CardContent></Card>}

      {!loading && items.length === 0 && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          No households yet.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {items.map((h) => (
          <Card key={h.id}>
            <CardContent className="p-0">
              <button onClick={() => void toggle(h.id)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 text-left">
                <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{h.name}</div>
                  <div className="text-xs text-muted-foreground">{h.primary_email ?? 'no email'}</div>
                </div>
                <Badge variant="outline">{h.active_count} active · {h.member_count} total</Badge>
              </button>
              {expanded === h.id && (
                <div className="border-t bg-muted/30">
                  {detail[h.id] ? (
                    <table className="w-full text-sm">
                      <thead className="text-xs text-muted-foreground">
                        <tr className="border-b">
                          <th className="text-left px-4 py-2 font-medium">Member</th>
                          <th className="text-left px-4 py-2 font-medium">Plan</th>
                          <th className="text-right px-4 py-2 font-medium">Price</th>
                          <th className="text-left px-4 py-2 font-medium">Status</th>
                          <th className="text-left px-4 py-2 font-medium">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail[h.id]!.map((m) => (
                          <tr key={m.id} className="border-b last:border-0">
                            <td className="px-4 py-2"><Link to={`/dashboard/members/${m.id}`} className="hover:underline font-medium">{m.name}</Link></td>
                            <td className="px-4 py-2 text-xs">{m.plan_name ?? '—'}</td>
                            <td className="px-4 py-2 text-right tabular-nums text-xs">{m.plan_amount_cents != null ? `${formatCents(m.plan_amount_cents)}/mo` : '—'}</td>
                            <td className="px-4 py-2"><Badge variant={m.status === 'active' ? 'default' : 'outline'} className="capitalize">{m.status}</Badge></td>
                            <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">{new Date(m.joined_at.replace(' ', 'T')).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="py-3 text-center text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /></div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Pagination meta={pageMeta} onPageChange={setPage} itemLabel="households" />
    </div>
  );
}
